import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ValidatedInput from "./ValidatedInput";

const BookingForm = ({
  availableTimes,
  dispatch,
  submitForm,
  resOccasionList,
  pageNum,
  setPageNum,
}) => {
  const [formData, setFormData] = useState({
    date: "",
    time: "00:00",
    guestCount: 1,
    occasion: "None",
    name: "",
    email: "",
    phone: "",
    textOk: false,
  });
  const [allRequiredFieldsValid, setAllRequiredFieldsValid] = useState(false);

  const handleFormChange = (name, value, isValid) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
    setAllRequiredFieldsValid(areAllRequiredFieldsValid());
  };

  useEffect(() => {
    setAllRequiredFieldsValid(areAllRequiredFieldsValid());
  }, [pageNum, formData]);

  const areAllRequiredFieldsValid = () => {
    // Page 1 validations
    if (pageNum === 1) {
      const isValidDate = formData.date !== "";
      const isValidTime = formData.time !== "00:00";
      const isValidGuestCount = formData.guestCount > 0;
      return isValidDate && isValidTime && isValidGuestCount;
    }

    // Page 2 validations
    else if (pageNum === 2) {
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      const isEmailValid = emailPattern.test(formData.email);
      const isNameValid = formData.name !== "";
      const isPhoneValid =
        !formData.textOk || (formData.textOk && formData.phone !== "");

      return isNameValid && isEmailValid && isPhoneValid;
    }

    // Default to true if not on Page 1 or Page 2
    return true;
  };

  const handleCheckChange = () => {
    const isTextOkChecked = !formData.textOk;
    setFormData({ ...formData, textOk: isTextOkChecked });
  };

  const handleDateChange = async (event) => {
    const { name, value } = event.target;

    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
    dispatch({ type: "UPDATE_TIMES", payload: value });
  };

  const isFormValid = () => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const isEmailValid = emailPattern.test(formData.email);
    const isNameValid = formData.name !== "";
    const isPhoneValid = formData.phone !== "";

    return isEmailValid && isNameValid && (!formData.textOk || isPhoneValid);
  };

  const handleNextPage = (event) => {
    event.preventDefault();
    if (pageNum < 4) {
      setPageNum(pageNum + 1);
    }
  };

  const handlePrevPage = (event) => {
    event.preventDefault();
    if (pageNum > 1) {
      setPageNum(pageNum - 1);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isFormValid()) {
      setPageNum(3);
      submitForm(formData);
    } else {
      console.log("Form is not valid");
    }
  };

  const handleFormClose = (event) => {
    event.preventDefault();
    navigate("/");
    setFormData({
      ...formData,
      date: "",
      time: "00:00",
      guestCount: 1,
      occasion: "None",
      name: "",
      email: "",
      phone: "",
      textOk: false,
    });
  };

  const navigate = useNavigate();

  const options = availableTimes.map((time) => (
    <option key={time}>{time}</option>
  ));

  const occasions = resOccasionList.map((occasion) => (
    <option key={occasion}>{occasion}</option>
  ));

  return (
    <div class="flex flex-col justify-center items-center p-5 bg-lemonyellow">
      {(pageNum === 1 || pageNum === 2 || pageNum === 3) && (
        <div
          class={`"max-w-4xl py-8 px-14 rounded-xl drop-shadow-md" ${
            (pageNum === 1 || pageNum === 2) && "border-4 border-black"
          }`}
        >
          {pageNum === 1 && (
            <form class="grid max-w-min gap-[20px]" onSubmit={handleNextPage}>
              <ValidatedInput
                inputLabel="Select Date *"
                inputName="date"
                inputType="date"
                inputRequired="true"
                inputValue={formData.date}
                onChange={(name, value, isValid) => {
                  handleDateChange({ target: { name, value } });
                  handleFormChange(name, value, isValid);
                }}
              />
              <ValidatedInput
                inputLabel="Select Time *"
                inputName="time"
                inputPlaceholder="Select a Time"
                inputType="select"
                inputRequired="true"
                inputValue={formData.time}
                onChange={handleFormChange}
              >
                {options}
              </ValidatedInput>
              <ValidatedInput
                inputLabel="Number of Guests *"
                inputName="guestCount"
                inputType="select"
                inputRequired="true"
                inputValue={formData.guestCount}
                onChange={handleFormChange}
              >
                {Array.from({ length: 10 }, (_, index) => (
                  <option key={index + 1} value={index + 1}>
                    {index + 1} Guest{index === 0 ? "" : "s"}
                  </option>
                ))}
              </ValidatedInput>
              <ValidatedInput
                inputLabel="Occasion"
                inputName="occasion"
                inputType="select"
                inputRequired="false"
                inputValue={formData.occasion}
                onChange={handleFormChange}
              >
                {occasions}
              </ValidatedInput>
              <input
                type="submit"
                value={!allRequiredFieldsValid ? "Missing Info" : "Book Now"}
                class={
                  !allRequiredFieldsValid ? "button-disabled" : "formbutton"
                }
                disabled={!allRequiredFieldsValid}
              />
            </form>
          )}
          {pageNum === 2 && (
            <form
              class="flex flex-col justify-center items-center max-w-min gap-[20px]"
              onSubmit={handleSubmit}
            >
              <ValidatedInput
                inputLabel="Name *"
                inputName="name"
                inputType="text"
                inputRequired="true"
                inputValue={formData.name}
                onChange={handleFormChange}
              />
              <ValidatedInput
                inputLabel="Email *"
                inputName="email"
                inputType="email"
                inputRequired="true"
                inputValue={formData.email}
                onChange={handleFormChange}
              />
              <ValidatedInput
                inputLabel={formData.textOk ? "Phone *" : "Phone"}
                inputName="phone"
                inputType="phone"
                inputRequired={formData.textOk}
                inputValue={formData.phone}
                onChange={handleFormChange}
              />
              <div class="flex flex-row text-xs space-x-2 -mt-5">
                <input
                  type="checkbox"
                  id="textOK"
                  name="textOk"
                  checked={formData.textOk}
                  onChange={handleCheckChange}
                />
                <label htmlFor="textOK">
                  Check if you would like to receive text communication
                  regarding this reservation
                </label>
              </div>

              <div class="flex flex-col justify-center items-center">
                <input
                  type="submit"
                  value={
                    !allRequiredFieldsValid
                      ? "Missing Info"
                      : "Book Reservation"
                  }
                  class={
                    !allRequiredFieldsValid ? "button-disabled" : "formbutton"
                  }
                  disabled={!allRequiredFieldsValid}
                />
              </div>
              <div class="flex space-x-4">
                <button onClick={handlePrevPage} class="formbutton">
                  Back
                </button>
              </div>
            </form>
          )}

          {/* Form 3, Confirmation */}
          {pageNum === 3 && (
            <div class="flex flex-col justify-center items-center max-w-min">
              <div class="flex flex-col justify-center items-center space-y-5 bg-lemongray rounded-xl w-[350px] p-8 text-sm drop-shadow-md shadow-md font-karla border-4 border-black radius-xl">
                <h1
                  class="font-bold"
                  aria-label="congratulations, your reservation is confirmed!"
                >
                  Congrats, your reservation is confirmed!
                </h1>
                <h2>
                  Here are your details,{" "}
                  <span class="font-bold" aria-label="Full Name">
                    {formData.name}
                  </span>
                  :
                </h2>
                <p>
                  {formData.occasion !== "None" ? (
                    <span class="font-bold">{formData.occasion}</span>
                  ) : (
                    " "
                  )}{" "}
                  Reservation for{" "}
                  <span class="font-bold">{formData.guestCount}</span>{" "}
                  {formData.guestCount > 1 ? "guests" : "guest"} on{" "}
                  <span class="font-bold">{formData.date}</span>, at{" "}
                  <span class="font-bold">{formData.time}</span>.
                </p>
                <p>
                  {formData.textOk ? (
                    <>
                      We will contact you at{" "}
                      <span className="font-bold">{formData.phone}</span> with
                      reservation updates.
                    </>
                  ) : (
                    "You have opted not to receive text notifications for this reservation"
                  )}
                </p>
                <p>
                  A confirmation of this reservation has been sent to:{" "}
                  <span class="font-bold">{formData.email}</span>
                </p>
                <p>We look forward to seeing you soon!</p>
                <button
                  onClick={handleFormClose}
                  class="lemonbutton hover:formbutton"
                >
                  Take Me Home
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BookingForm;
